import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CountUp from 'react-countup';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AnnouncementCard } from '../components/AnnouncementCard';
import { formatCurrency } from '../lib/formatCurrency';
import { getDashboardLive } from '../lib/api';
import {
  Users, TrendingUp, Ticket, Briefcase, FileText, CheckCircle2,
  ArrowUpRight, ArrowDownRight, Download, CalendarRange, Trophy,
  AlertCircle, Clock, Plus, UserPlus, BarChart3, Zap, Pencil, Trash2,
  Paperclip, Activity, FolderPlus, Eye
} from 'lucide-react';
import {
  AreaChart, Area, ComposedChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: 'month', label: 'This month', days: 'month' },
  { key: '90d', label: 'Last 90 days', days: 90 },
];

const rangeDates = (key) => {
  const end = new Date();
  const start = new Date();
  const preset = RANGES.find(r => r.key === key) || RANGES[2];
  if (preset.days === 'month') start.setDate(1);
  else start.setDate(end.getDate() - (preset.days - 1));
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { start: fmt(start), end: fmt(end) };
};

const DONUT_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', completed: '#10b981', overdue: '#ef4444' };

const ACTIVITY_META = {
  create: { icon: Plus, color: 'bg-blue-500' },
  edit: { icon: Pencil, color: 'bg-amber-500' },
  delete: { icon: Trash2, color: 'bg-rose-500' },
  download: { icon: Download, color: 'bg-emerald-500' },
  attach_file: { icon: Paperclip, color: 'bg-violet-500' },
  folder_create: { icon: FolderPlus, color: 'bg-cyan-500' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return d.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
};

const KpiCard = ({ testid, title, value, change, spark, icon: Icon, tint, sub, prefix = '', onClick }) => {
  const up = change >= 0;
  return (
    <Card className="kpi-card cursor-pointer group !p-5" onClick={onClick} data-testid={testid}>
      <CardContent className="p-0 space-y-3">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${tint.bg}`}>
            <Icon className={`h-5 w-5 ${tint.fg}`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground truncate">{title}</p>
            <p className="text-2xl font-bold font-mono leading-tight">
              {prefix}<CountUp end={value} duration={0.8} separator="," preserveValue />
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs">
          {up ? <ArrowUpRight className="h-3.5 w-3.5 text-emerald-500" /> : <ArrowDownRight className="h-3.5 w-3.5 text-rose-500" />}
          <span className={`font-semibold ${up ? 'text-emerald-500' : 'text-rose-500'}`}>{Math.abs(change)}%</span>
          <span className="text-muted-foreground">vs prev period</span>
        </div>
        {sub && <p className="text-xs text-muted-foreground truncate">{sub}</p>}
        <div className="h-9 w-full overflow-hidden">
          <ResponsiveContainer width="100%" height={36}>
            <AreaChart data={(spark || []).map((v, i) => ({ i, v }))} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
              <Area type="monotone" dataKey="v" stroke={tint.line} strokeWidth={1.5} fill={tint.line} fillOpacity={0.12} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState('30d');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [exporting, setExporting] = useState(false);
  const rangeRef = useRef(rangeKey);
  rangeRef.current = rangeKey;

  const load = useCallback(async (silent = false) => {
    if (document.hidden && silent) return;
    try {
      const res = await getDashboardLive(rangeDates(rangeRef.current));
      setData(res.data);
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Dashboard load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [rangeKey, load]);

  useEffect(() => {
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [load]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/dashboard/live/export`, {
        params: { ...rangeDates(rangeKey), format }, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dashboard_report.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="h-10 w-64 rounded-xl bg-muted animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-44 rounded-2xl bg-muted animate-pulse" />)}
        </div>
        <div className="grid xl:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      </div>
    );
  }

  const k = data?.kpis || {};
  const ts = data?.task_status || {};
  const donut = ['open', 'in_progress', 'completed', 'overdue']
    .map(key => ({ key, name: key.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()), value: ts[key] || 0 }))
    .filter(d => d.value > 0);
  const donutTotal = ts.total || 0;
  const isAgent = user?.role === 'agent';
  const firstName = (user?.name || '').split(' ')[0];
  const trophyColors = ['text-amber-500', 'text-slate-400', 'text-orange-600'];

  const QUICK_ACTIONS = [
    { label: 'Add Task', icon: Plus, to: '/tasks', testid: 'quick-add-task' },
    { label: 'Add Customer', icon: UserPlus, to: '/clients', testid: 'quick-add-customer' },
    { label: 'Create Ticket', icon: Ticket, to: '/projects', testid: 'quick-create-ticket' },
    { label: 'Generate Report', icon: BarChart3, action: () => handleExport('pdf'), testid: 'quick-generate-report' },
  ];

  return (
    <div className="space-y-6" data-testid="live-dashboard">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
          <div className="text-muted-foreground mt-1 flex items-center gap-2">
            <span>Welcome back, {firstName} 👋</span> {isAgent && <Badge variant="secondary">My data</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground mr-1" data-testid="live-indicator">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            Live{lastUpdated ? ` · ${lastUpdated.toLocaleTimeString()}` : ''}
          </span>
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="w-40 rounded-xl h-10" data-testid="date-range-select">
              <CalendarRange className="h-4 w-4 mr-1 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-xl h-10 gap-2" disabled={exporting} data-testid="export-report-btn">
                <Download className="h-4 w-4" />
                {exporting ? 'Exporting…' : 'Export Report'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">Export as PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx-btn">Export as Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard testid="kpi-total-customers" title="Total Customers" value={k.total_customers?.value || 0}
          change={k.total_customers?.change || 0} spark={k.total_customers?.spark} icon={Users}
          tint={{ bg: 'bg-blue-100 dark:bg-blue-500/15', fg: 'text-blue-600 dark:text-blue-400', line: '#3b82f6' }}
          onClick={() => navigate('/clients')} />
        <KpiCard testid="kpi-new-leads" title="New Leads" value={k.new_leads?.value || 0}
          change={k.new_leads?.change || 0} spark={k.new_leads?.spark} icon={TrendingUp}
          tint={{ bg: 'bg-emerald-100 dark:bg-emerald-500/15', fg: 'text-emerald-600 dark:text-emerald-400', line: '#10b981' }}
          onClick={() => navigate('/leads')} />
        <KpiCard testid="kpi-open-tickets" title="Open Tickets" value={k.open_tickets?.value || 0}
          change={k.open_tickets?.change || 0} spark={k.open_tickets?.spark} icon={Ticket}
          sub={`${k.open_tickets?.closed || 0} closed`}
          tint={{ bg: 'bg-violet-100 dark:bg-violet-500/15', fg: 'text-violet-600 dark:text-violet-400', line: '#8b5cf6' }}
          onClick={() => navigate('/projects')} />
        <KpiCard testid="kpi-active-projects" title="Active Projects" value={k.active_projects?.value || 0}
          change={k.active_projects?.change || 0} spark={k.active_projects?.spark} icon={Briefcase}
          tint={{ bg: 'bg-orange-100 dark:bg-orange-500/15', fg: 'text-orange-600 dark:text-orange-400', line: '#f59e0b' }}
          onClick={() => navigate('/projects')} />
        <KpiCard testid="kpi-pending-invoices" title="Pending Invoices" value={k.pending_invoices?.value || 0}
          change={k.pending_invoices?.change || 0} spark={k.pending_invoices?.spark} icon={FileText}
          sub={`${formatCurrency(k.pending_invoices?.amount || 0)} outstanding`}
          tint={{ bg: 'bg-cyan-100 dark:bg-cyan-500/15', fg: 'text-cyan-600 dark:text-cyan-400', line: '#06b6d4' }}
          onClick={() => navigate('/finance?tab=invoices')} />
        <KpiCard testid="kpi-tasks-completed" title="Tasks Completed" value={k.tasks_completed?.value || 0}
          change={k.tasks_completed?.change || 0} spark={k.tasks_completed?.spark} icon={CheckCircle2}
          tint={{ bg: 'bg-pink-100 dark:bg-pink-500/15', fg: 'text-pink-600 dark:text-pink-400', line: '#ec4899' }}
          onClick={() => navigate('/tasks')} />
      </div>

      {/* Row 2 */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Task Status Donut */}
        <Card className="chart-container !p-0" data-testid="task-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Task Status Overview</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            <div className="relative h-52 w-52 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut.length ? donut : [{ key: 'none', name: 'No tasks', value: 1 }]}
                    dataKey="value" innerRadius={62} outerRadius={95} paddingAngle={2} strokeWidth={0}>
                    {(donut.length ? donut : [{ key: 'none' }]).map(d => (
                      <Cell key={d.key} fill={DONUT_COLORS[d.key] || '#e2e8f0'} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="text-2xl font-bold font-mono">{donutTotal}</span>
                <span className="text-xs text-muted-foreground">Tasks</span>
              </div>
            </div>
            <div className="space-y-3 min-w-0">
              {['open', 'in_progress', 'completed', 'overdue'].map(key => (
                <div key={key} className="flex items-center gap-2 text-sm" data-testid={`task-status-${key}`}>
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[key] }} />
                  <span className="capitalize text-muted-foreground">{key.replace('_', ' ')}</span>
                  <span className="font-semibold font-mono ml-auto">
                    {ts[key] || 0} <span className="text-muted-foreground font-normal">({donutTotal ? Math.round((ts[key] || 0) / donutTotal * 100) : 0}%)</span>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Employee Performance */}
        <Card className="chart-container !p-0" data-testid="employee-performance-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Employee Performance</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.employee_performance?.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={data.employee_performance} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} tickFormatter={(n) => (n || '').split(' ')[0]} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="tasks_completed" name="Tasks Completed" fill="#3b82f6" radius={[6, 6, 0, 0]} barSize={22} />
                  <Line dataKey="score" name="Score %" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex items-center justify-center text-sm text-muted-foreground">No performance data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Overdue Tasks */}
        <Card className="chart-container !p-0 lg:col-span-2 xl:col-span-1" data-testid="overdue-tasks-card">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold">Overdue Tasks</CardTitle>
            <Button variant="ghost" size="sm" className="text-primary h-7" onClick={() => navigate('/tasks')} data-testid="overdue-view-all">View All</Button>
          </CardHeader>
          <CardContent className="space-y-1">
            {data?.overdue_tasks?.length ? data.overdue_tasks.map((t, i) => (
              <div key={t.id || i} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`overdue-task-${i}`}>
                <div className="h-8 w-8 rounded-lg bg-rose-100 dark:bg-rose-500/15 flex items-center justify-center shrink-0">
                  <AlertCircle className="h-4 w-4 text-rose-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{t.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.assignee ? `${t.assignee} · ` : ''}Due {t.due_date}</p>
                </div>
                <Badge variant="secondary" className="bg-rose-100 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400 shrink-0">
                  {t.days_overdue}d
                </Badge>
              </div>
            )) : (
              <div className="h-40 flex flex-col items-center justify-center text-sm text-muted-foreground gap-2">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                No overdue tasks 🎉
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3 */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Activity Timeline */}
        <Card className="chart-container !p-0" data-testid="activity-timeline-card">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold">Activity Timeline</CardTitle>
            <Badge variant="secondary" className="font-normal">Latest</Badge>
          </CardHeader>
          <CardContent className="space-y-1">
            {data?.activity_timeline?.length ? data.activity_timeline.slice(0, 6).map((a, i) => {
              const meta = ACTIVITY_META[a.type] || { icon: Activity, color: 'bg-slate-400' };
              const MIcon = meta.icon;
              return (
                <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`activity-item-${i}`}>
                  <div className={`h-8 w-8 rounded-lg ${meta.color} flex items-center justify-center shrink-0`}>
                    <MIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{a.user} <span className="font-normal text-muted-foreground">{a.type.replace(/_/g, ' ')}</span> {a.record}</p>
                    <p className="text-xs text-muted-foreground">{timeAgo(a.time)}{a.record_type ? ` · ${a.record_type}` : ''}</p>
                  </div>
                </div>
              );
            }) : (
              <div className="h-40 flex items-center justify-center text-sm text-muted-foreground">No recent activity</div>
            )}
          </CardContent>
        </Card>

        {/* Performance Trend */}
        <Card className="chart-container !p-0" data-testid="performance-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Performance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data?.performance_trend || []} margin={{ top: 5, right: 5, bottom: 0, left: -25 }}>
                <defs>
                  <linearGradient id="gTasks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="tasks_completed" name="Tasks Completed" stroke="#3b82f6" strokeWidth={2} fill="url(#gTasks)" />
                <Area type="monotone" dataKey="leads_created" name="Leads Created" stroke="#10b981" strokeWidth={2} fill="url(#gLeads)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Performers + Quick Actions */}
        <div className="space-y-4 lg:col-span-2 xl:col-span-1">
          <Card className="chart-container !p-0" data-testid="top-performers-card">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-semibold">Top Performers</CardTitle>
              <Button variant="ghost" size="sm" className="text-primary h-7" onClick={() => navigate('/team-management')} data-testid="performers-view-all">View All</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {data?.top_performers?.length ? data.top_performers.map((p, i) => (
                <div key={p.name} className="flex items-center gap-3 py-1.5" data-testid={`top-performer-${i}`}>
                  <span className="text-sm font-bold font-mono w-4 text-muted-foreground">{i + 1}</span>
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                    {(p.name || '?').charAt(0)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">Tasks: {p.tasks_completed} · Score: {p.score}%</p>
                  </div>
                  <Trophy className={`h-5 w-5 shrink-0 ${trophyColors[i]}`} />
                </div>
              )) : (
                <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">No data yet</div>
              )}
            </CardContent>
          </Card>

          <Card className="chart-container !p-0" data-testid="quick-actions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              {QUICK_ACTIONS.map(qa => {
                const QIcon = qa.icon;
                return (
                  <button key={qa.label}
                    onClick={() => qa.action ? qa.action() : navigate(qa.to)}
                    className="flex items-center gap-2 p-3 rounded-xl border bg-muted/40 hover:bg-accent hover:border-primary/30 transition-colors text-sm font-medium text-left"
                    data-testid={qa.testid}>
                    <QIcon className="h-4 w-4 text-primary shrink-0" />
                    <span className="truncate">{qa.label}</span>
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Announcements (admins) */}
      <AnnouncementCard />
    </div>
  );
};
